import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  NotificationCategory,
  NotificationChannel,
  NotificationPreference,
} from "@/model/interface/NotificationPreference";
import { useGetNotificationPreferencesQuery, useUpdateNotificationPreferencesMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

const CHANNELS: { key: NotificationChannel; label: string }[] = [
  { key: "IN_APP", label: "In app" },
  { key: "EMAIL", label: "Email" },
  { key: "PUSH", label: "Push" },
];

const GROUPS: { title: string; categories: NotificationCategory[] }[] = [
  { title: "Conversations", categories: ["INCOMING_CALL", "INCOMING_SMS"] },
  { title: "Your work", categories: ["TASK", "DEAL", "MENTION", "REMINDER"] },
  { title: "Automation", categories: ["WORKFLOW", "CAMPAIGN"] },
];

const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  INCOMING_CALL: "Missed calls",
  INCOMING_SMS: "Text messages",
  TASK: "Tasks",
  DEAL: "Deals",
  MENTION: "Mentions",
  WORKFLOW: "Workflows",
  CAMPAIGN: "Campaigns",
  REMINDER: "Reminders",
};

const CATEGORY_DESCRIPTIONS: Partial<Record<NotificationCategory, string>> = {
  MENTION: "When a teammate mentions you in a note.",
  WORKFLOW: "When an automation you own finishes or stops on an error.",
  CAMPAIGN: "Start, pause and completion of a calling or texting campaign.",
};

const cellKey = (category: NotificationCategory, channel: NotificationChannel) => `${category}:${channel}`;

export default function NotificationPreferencesCard() {
  const { data: preferences, isLoading } = useGetNotificationPreferencesQuery();
  const [savePreferences] = useUpdateNotificationPreferencesMutation();
  const [activeChannel, setActiveChannel] = useState<NotificationChannel>("IN_APP");
  const [matrix, setMatrix] = useState<Record<string, boolean>>({});

  const enabledCount = (channel: NotificationChannel) => {
    return GROUPS.flatMap((group) => group.categories).filter((category) => matrix[cellKey(category, channel)])
      .length;
  };

  const onToggle = async (category: NotificationCategory, channel: NotificationChannel) => {
    const key = cellKey(category, channel);
    const next = !matrix[key];
    setMatrix((current) => ({ ...current, [key]: next }));

    const item: NotificationPreference = {
      category,
      channel,
      status: next ? "ENABLED" : "DISABLED",
    };
    try {
      await savePreferences([item]).unwrap();
    } catch (error: unknown) {
      setMatrix((current) => ({ ...current, [key]: !next }));
      toast.error(apiErrorMessage(error, "Failed to save your notification preferences"));
    }
  };

  useEffect(() => {
    if (preferences) {
      const next: Record<string, boolean> = {};
      preferences.forEach((preference) => {
        next[cellKey(preference.category, preference.channel)] = preference.status === "ENABLED";
      });
      setMatrix(next);
    }
  }, [preferences]);

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>
          Pick a channel, then choose what reaches you on it. Changes save as you make them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeChannel} onValueChange={(value) => setActiveChannel(value as NotificationChannel)}>
          <TabsList>
            {CHANNELS.map((channel) => (
              <TabsTrigger key={channel.key} value={channel.key} className="gap-2">
                {channel.label}
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums">
                  {enabledCount(channel.key)}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {GROUPS.map((group) => (
          <React.Fragment key={group.title}>
            <div className="pt-5 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {group.title}
            </div>
            {group.categories.map((category) => (
              <div key={category} className="flex min-h-12 items-center gap-4 border-t py-2">
                <div className="flex-1">
                  <span className="block text-sm font-semibold">{CATEGORY_LABELS[category]}</span>
                  {CATEGORY_DESCRIPTIONS[category] && (
                    <span className="mt-0.5 block text-sm text-muted-foreground">
                      {CATEGORY_DESCRIPTIONS[category]}
                    </span>
                  )}
                </div>
                <Switch
                  checked={!!matrix[cellKey(category, activeChannel)]}
                  onCheckedChange={() => onToggle(category, activeChannel)}
                  aria-label={`${CATEGORY_LABELS[category]} on ${
                    CHANNELS.find((channel) => channel.key === activeChannel)?.label
                  }`}
                />
              </div>
            ))}
          </React.Fragment>
        ))}
      </CardContent>
    </Card>
  );
}
